// Privacy.js
// Política de privacidad, español e inglés. #added 3/11/26 TG
// El texto viene de proposals/privacy_page_draft.md (repo timercrossfit).
// Si cambia algo importante: actualizar el texto, la fecha, y los términos de la app. TG

import React, { useContext, useEffect } from "react";
import { AppContext } from "./ContextVars";

const EMAIL = "muscles@eleven.fit";
const Mail = () => <a href={`mailto:${EMAIL}`} style={{ textDecoration: "underline" }}>{EMAIL}</a>;
const DbIp = () => <a href="https://db-ip.com" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline" }}>DB-IP</a>;

const H2 = ({ children }) => <h2 style={{ fontSize: 22, fontWeight: 600, margin: "32px 0 10px" }}>{children}</h2>;
const P = ({ children }) => <p style={{ margin: "0 0 12px" }}>{children}</p>;
const UL = ({ children }) => <ul style={{ margin: "0 0 12px", paddingLeft: 22, listStyle: "disc" }}>{children}</ul>;
const LI = ({ children }) => <li style={{ marginBottom: 8 }}>{children}</li>;

const Spanish = () => (
  <>
    <h1 style={{ fontSize: 34, fontWeight: 600, marginBottom: 8 }}>Política de privacidad</h1>
    <P>Última actualización: 3 de noviembre de 2026</P>
    <P>Esta página explica qué datos guarda Eleven.Fit, para qué, y con quién los compartimos. Cubre el sitio eleven.fit y la app de entrenamiento. El responsable es Oscar Romeo Gálvez Mont, Guatemala. Para cualquier pregunta sobre tus datos: <Mail />.</P>

    <H2>Cuando visitas el sitio</H2>
    <P>Guardamos un registro de visitas para saber qué páginas se usan y de dónde llega la gente:</P>
    <UL>
      <LI>Las páginas que abres, la página o el enlace del que vienes, y los datos de campaña que traiga el enlace.</LI>
      <LI>Un identificador al azar guardado en tu navegador, que nos deja reconocer visitas repetidas sin saber tu nombre.</LI>
      <LI>Tu idioma, el tamaño de tu pantalla, y tu tipo de dispositivo, navegador y sistema.</LI>
      <LI>Tu país aproximado, que sale de tu dirección IP. No guardamos la IP en el registro de visitas, solo una versión codificada. Lo calculamos en nuestro propio servidor con datos de <DbIp />, sin mandarle tu IP a nadie. Los registros más antiguos también pueden mostrar región y ciudad aproximadas.</LI>
    </UL>

    <H2>Cuando compras</H2>
    <P>Para procesar tu pedido y activar tu programa guardamos tu nombre, apellido, correo, teléfono, ciudad, país, lo que compraste, el monto y el número de orden.</P>
    <UL>
      <LI><strong>Tarjeta o PayPal:</strong> escribes tus datos de pago en la página de Tilopay o de PayPal. Tu número de tarjeta y tu código de seguridad nunca llegan a nuestro servidor. Ellos nos mandan el resultado del pago y lo guardamos con tu orden.</LI>
      <LI><strong>Transferencia bancaria:</strong> guardamos el comprobante que subes y el número de transferencia, para poder revisar el pago. El comprobante puede mostrar tu nombre y datos de tu cuenta, así que solo se puede ver desde nuestro panel de administración, con contraseña.</LI>
    </UL>
    <P>También te mandamos correos sobre tu pedido.</P>

    <H2>Publicidad: Meta</H2>
    <P>Usamos el píxel de Meta (Facebook e Instagram) para medir nuestros anuncios y mostrarlos a personas a las que les puedan interesar. Meta también puede usar estos datos según su propia política.</P>
    <UL>
      <LI><strong>En el navegador:</strong> el píxel le dice a Meta qué páginas visitas y qué productos ves o agregas al carrito. Meta puede guardar cookies en tu navegador para esto. El píxel no corre en la página de confirmación de pago ni en la de transferencia bancaria.</LI>
      <LI><strong>Desde nuestro servidor, cuando una compra se paga:</strong> le mandamos a Meta el monto, la moneda, el número de orden y el producto. También tu correo, teléfono, nombre, apellido, ciudad y país, codificados con un hash (SHA-256) antes de salir de nuestro servidor. Cuando se puede, agregamos tu dirección IP, los datos de tu navegador y las cookies de Meta, sin codificar.</LI>
    </UL>
    <P>Puedes controlar los anuncios que ves en la configuración de anuncios de tu cuenta de Facebook o Instagram, y borrar o bloquear las cookies desde tu navegador.</P>

    <H2>Otros servicios que cargan en el sitio</H2>
    <P>Algunas páginas cargan cosas de otras empresas, que reciben tu IP y los datos normales de tu navegador: Google Fonts para las letras, YouTube para los videos, y Tilopay para los pagos. Cada una tiene su propia política de privacidad.</P>

    <H2>En la app de entrenamiento</H2>
    <P>La app guarda lo que necesita para ser tu bitácora de entrenamiento:</P>
    <UL>
      <LI>Tu cuenta: nombre, correo, tu contraseña como hash (no podemos leerla), icono, idioma y nivel.</LI>
      <LI>Tu entrenamiento: sesiones, tiempos, notas, pesos, reps, calorías, workouts y planes.</LI>
      <LI>Si decides registrarlas: medidas corporales y lesiones o condiciones. Son solo tuyas, un coach no las puede ver.</LI>
      <LI>Las fotos y capturas que subes, los comentarios, reacciones, calificaciones y seguidores, y tus conversaciones con la IA.</LI>
      <LI>Un registro limitado de intentos de login, para frenar ataques.</LI>
    </UL>
    <P><strong>Inteligencia artificial:</strong> cuando usas una función de IA, el texto correspondiente (el workout, tu pregunta) y, si subes una captura, la imagen, se le mandan a un proveedor externo: OpenAI, Anthropic o DeepSeek. Tu correo y tu contraseña nunca se mandan.</P>
    <P>La app no lleva el píxel de Meta. Los detalles están en los Términos y Condiciones de la app.</P>

    <H2>Con quién compartimos datos</H2>
    <P>No vendemos tus datos. Solo los reciben las empresas que nos ayudan a operar: Tilopay y PayPal (pagos), Meta (publicidad), OpenAI, Anthropic y DeepSeek (IA), y nuestros proveedores de hosting y de correo. También los entregaríamos si la ley nos obliga. Algunas de estas empresas están fuera de tu país, por ejemplo en Estados Unidos o China.</P>

    <H2>Cuánto tiempo los guardamos</H2>
    <UL>
      <LI>Registro de visitas: 14 meses.</LI>
      <LI>Pedidos y comprobantes: 5 años, por contabilidad.</LI>
      <LI>Tu cuenta de la app: hasta que pidas que la borremos. Borrar algo dentro de la app lo esconde, pero no lo elimina de inmediato, para poder deshacer un error.</LI>
    </UL>

    <H2>Tus opciones</H2>
    <P>Escríbenos a <Mail /> para ver, corregir o borrar tus datos. Te pedimos escribir desde el correo que usaste. Podemos conservar lo que la ley o la contabilidad nos pidan guardar.</P>

    <H2>Menores de edad</H2>
    <P>Si eres menor de 18 años, usa Eleven.Fit solo con el conocimiento y el permiso de tu padre, madre o tutor.</P>

    <H2>Cambios</H2>
    <P>Si cambiamos algo importante, actualizamos esta página y su fecha.</P>
  </>
);

const English = () => (
  <>
    <h1 style={{ fontSize: 34, fontWeight: 600, marginBottom: 8 }}>Privacy policy</h1>
    <P>Last updated: November 3, 2026</P>
    <P>This page explains what data Eleven.Fit keeps, why, and who we share it with. It covers the eleven.fit site and the training app. The party responsible is Oscar Romeo Gálvez Mont, Guatemala. For any question about your data: <Mail />.</P>

    <H2>When you visit the site</H2>
    <P>We keep a record of visits, to know which pages get used and where people come from:</P>
    <UL>
      <LI>The pages you open, the page or link you came from, and any campaign details in that link.</LI>
      <LI>A random ID stored in your browser, which lets us recognize repeat visits without knowing your name.</LI>
      <LI>Your language, screen size, and type of device, browser and operating system.</LI>
      <LI>Your approximate country, worked out from your IP address. The visit record doesn't keep your IP, only a scrambled version of it. We work it out on our own server with data from <DbIp />, without sending your IP to anyone. Older records may also show an approximate region and city.</LI>
    </UL>

    <H2>When you buy</H2>
    <P>To process your order and activate your program, we keep your first and last name, email, phone, city, country, what you bought, the amount and the order number.</P>
    <UL>
      <LI><strong>Card or PayPal:</strong> you enter your payment details on Tilopay's or PayPal's page. Your card number and security code never reach our server. They send us the payment result, and we keep it with your order.</LI>
      <LI><strong>Bank transfer:</strong> we keep the proof you upload and the transfer number, so we can check the payment. The proof may show your name and account details, so it can only be viewed from our admin panel, with a password.</LI>
    </UL>
    <P>We also email you about your order.</P>

    <H2>Advertising: Meta</H2>
    <P>We use the Meta pixel (Facebook and Instagram) to measure our ads and show them to people who may be interested. Meta may also use this data under its own policy.</P>
    <UL>
      <LI><strong>In your browser:</strong> the pixel tells Meta which pages you visit and which products you view or add to your cart. Meta may store cookies in your browser for this. The pixel doesn't run on the payment confirmation page or the bank transfer page.</LI>
      <LI><strong>From our server, when a purchase is paid:</strong> we send Meta the amount, currency, order number and product. Also your email, phone, first name, last name, city and country, hashed (SHA-256) before they leave our server. When available, we add your IP address, browser details and Meta's cookies, not hashed.</LI>
    </UL>
    <P>You can control the ads you see in the ad settings of your Facebook or Instagram account, and delete or block cookies in your browser.</P>

    <H2>Other services that load on the site</H2>
    <P>Some pages load things from other companies, which receive your IP and normal browser details: Google Fonts for lettering, YouTube for videos, and Tilopay for payments. Each has its own privacy policy.</P>

    <H2>In the training app</H2>
    <P>The app keeps what it needs to be your training log:</P>
    <UL>
      <LI>Your account: name, email, your password as a hash (we can't read it), icon, language and level.</LI>
      <LI>Your training: sessions, times, notes, weights, reps, calories, workouts and plans.</LI>
      <LI>If you choose to record them: body measurements and injuries or conditions. These are yours alone, a coach can't see them.</LI>
      <LI>Photos and screenshots you upload, comments, reactions, ratings and follows, and your conversations with the AI.</LI>
      <LI>A limited record of login attempts, to stop attacks.</LI>
    </UL>
    <P><strong>AI:</strong> when you use an AI feature, the relevant text (the workout, your question) and, if you upload a screenshot, the image, are sent to an outside provider: OpenAI, Anthropic or DeepSeek. Your email and password are never sent.</P>
    <P>The app doesn't carry the Meta pixel. The details are in the app's Terms & Conditions.</P>

    <H2>Who we share data with</H2>
    <P>We don't sell your data. It only goes to the companies that help us operate: Tilopay and PayPal (payments), Meta (advertising), OpenAI, Anthropic and DeepSeek (AI), and our hosting and email providers. We'd also hand it over if the law requires it. Some of these companies are outside your country, for example in the United States or China.</P>

    <H2>How long we keep it</H2>
    <UL>
      <LI>Visit records: 14 months.</LI>
      <LI>Orders and payment proofs: 5 years, for accounting.</LI>
      <LI>Your app account: until you ask us to delete it. Deleting something in the app hides it but doesn't erase it right away, so a mistake can be undone.</LI>
    </UL>

    <H2>Your choices</H2>
    <P>Write to <Mail /> to see, correct or delete your data. Please write from the email address you used. We may keep what the law or accounting requires us to keep.</P>

    <H2>Minors</H2>
    <P>If you're under 18, use Eleven.Fit only with the knowledge and permission of a parent or guardian.</P>

    <H2>Changes</H2>
    <P>If we change something important, we update this page and its date.</P>
  </>
);

const Privacy = () => {
  const { language } = useContext(AppContext);

  // El link del pie de página llega con el scroll abajo; que abra desde arriba. TG
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main style={{ maxWidth: 760, margin: "0 auto", padding: "110px 20px 40px", textAlign: "left", lineHeight: 1.6 }}>
      {language === "en" ? <English /> : <Spanish />}
    </main>
  );
};

export default Privacy;
